import classNames from 'classnames/bind';
import React, { forwardRef, ReactElement } from 'react';
import { SortableWidgetProps, Widget } from '@creditsights/styleguide-web';
import { NO_DATA } from '../../../Base.constants';
import useFetchFinancials from '../../../hooks/financials/useFetchFinancials';
import useFetchFilters from '../../../hooks/tags/useFetchFilters';
import { PageComponent } from '../../../models';
import { Filter, FilterItem, FilterType } from '../../../models/Filter';
import { IssuerDto } from '../../../services/dtos/issuer/issuer.dto';
import DateFooter from '../common/DateFooter';
import styles from './CompanyOverview.module.scss';
import CompanyOverviewSectors from './CompanyOverviewSectors';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import { hasPermissionToRp } from '../../../utils/permissionsUtils';

const cx = classNames.bind(styles);

export interface CompanyOverviewWidgetProps extends SortableWidgetProps {
  company: IssuerDto;
  companyCoverageComponent?: PageComponent;
  noPrint?: boolean;
  metadataOptions?: FilterType[];
  showDescription?: boolean;
  showAsOfDate?: boolean;
}

const CompanyOverviewWidget = (
  {
    company,
    companyCoverageComponent,
    noPrint,
    metadataOptions,
    showDescription = true,
    showAsOfDate = true,
    ...sortableWidgetProps
  }: CompanyOverviewWidgetProps,
  ref
): ReactElement => {
  const { data: tagFilters, isLoading } = useFetchFilters([
    FilterType.sector,
    FilterType.creditGrade,
    FilterType.region,
  ]);
  const { data: userPermissions } = useFetchUserPermissions();
  const { data: financialsData } = useFetchFinancials(company.tagId, !hasPermissionToRp(userPermissions));
  const showFinancialsData = company.financialsCoverage && !companyCoverageComponent?.body;
  const businessDescription = showFinancialsData
    ? financialsData?.business_description
    : companyCoverageComponent?.body;
  const asOfDate = showFinancialsData ? financialsData?.published_date : companyCoverageComponent?.modifiedDate;

  const showMetadata = (type: FilterType) => !metadataOptions || metadataOptions.includes(type);

  const getTagFilter = (type: FilterType) => tagFilters?.find((tagFilter) => tagFilter.type === type);

  const renderMetadataField = (tagFilter: Filter | undefined): ReactElement | ReactElement[] => {
    const companyMetadata: FilterItem[] =
      tagFilter?.options.filter((option) => company.implied.includes(option.tagId)) || [];

    if (!companyMetadata.length) {
      return <div className={cx('cell')}>{NO_DATA}</div>;
    }

    return companyMetadata.map((value) => (
      <div className={cx('cell', { multiple: companyMetadata.length > 1 })} key={value.tagId}>
        {value.displayName}
      </div>
    ));
  };

  const renderSummary = (): ReactElement | undefined => {
    if (!businessDescription || !showDescription) {
      return undefined;
    }

    return (
      <div
        className={cx('description')}
        dangerouslySetInnerHTML={{ __html: businessDescription || '' }}
        data-testid="business-description"
      />
    );
  };

  return (
    <Widget
      headerTitle="Company Overview"
      classNames={cx({ 'no-print': noPrint })}
      isLoading={isLoading}
      dataTestId="company-overview-widget"
      footer={
        asOfDate && showAsOfDate ? (
          <div className="to-translate">
            <DateFooter date={asOfDate} dataTestId="company-overview-as-of-date" />
          </div>
        ) : null
      }
      ref={ref}
      {...sortableWidgetProps}
    >
      <div className={classNames(cx('body'), 'to-translate')} data-testid="company-overview-content">
        <div className={cx('metadata')} data-testid="company-metadata">
          {showMetadata(FilterType.region) && (
            <div className={cx('row')}>
              <div className={cx('cell')}>Region</div>
              <div data-testid="metadata-regions">{renderMetadataField(getTagFilter(FilterType.region))}</div>
            </div>
          )}
          {showMetadata(FilterType.sector) && (
            <CompanyOverviewSectors filter={getTagFilter(FilterType.sector)} company={company} />
          )}
          {showMetadata(FilterType.creditGrade) && (
            <div className={cx('row', { 'hide-border': !businessDescription })}>
              <div className={cx('cell')}>Credit Grade</div>
              <div data-testid="metadata-credit-grade">{renderMetadataField(getTagFilter(FilterType.creditGrade))}</div>
            </div>
          )}
        </div>
        {renderSummary()}
      </div>
    </Widget>
  );
};

export default forwardRef(CompanyOverviewWidget);
